import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { createPortal } from 'react-dom';
import toast from 'react-hot-toast';
import { useAppDispatch } from '../store/hooks';
import { changePassword } from '../store/slices/authSlice';

interface ChangePasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setError(null);
    }
  }, [isOpen]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !isSubmitting) onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!currentPassword.trim()) {
      setError(t('header.changePasswordCurrentRequired'));
      return;
    }
    if (!newPassword.trim()) {
      setError(t('header.changePasswordNewRequired'));
      return;
    }
    if (newPassword !== confirmPassword) {
      setError(t('header.changePasswordConfirmMismatch'));
      return;
    }

    setIsSubmitting(true);
    try {
      await dispatch(
        changePassword({
          currentPassword: currentPassword.trim(),
          newPassword: newPassword.trim(),
        })
      ).unwrap();
      toast.success(t('header.changePasswordSuccess'));
      onClose();
    } catch (err) {
      const message = typeof err === 'string' ? err : (err as Error)?.message ?? t('header.changePasswordError');
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-[9998] transition-opacity"
        onClick={handleBackdropClick}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%' }}
      />
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-2 sm:p-4"
        style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, width: '100%', height: '100%' }}
      >
        <div
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 w-full max-w-md overflow-hidden flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-4 lg:px-6 py-3 lg:py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between flex-shrink-0">
            <h2 className="text-lg lg:text-xl font-semibold text-gray-900 dark:text-gray-100">
              {t('header.changePasswordModalTitle')}
            </h2>
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors disabled:opacity-50"
              aria-label={t('common.close')}
            >
              <i className="fa-solid fa-times text-xl" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-4 lg:p-6 space-y-4">
            {error && (
              <p className="text-sm text-red-600 dark:text-red-400" role="alert">
                {error}
              </p>
            )}
            <div>
              <label htmlFor="change-password-current" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('header.changePasswordCurrentLabel')}
              </label>
              <div className="relative">
                <input
                  id="change-password-current"
                  type="text"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent ${!showCurrent ? 'input-mask-password' : ''}`}
                  placeholder={t('header.changePasswordCurrentPlaceholder')}
                  autoComplete="off"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  aria-label={showCurrent ? t('forms.password.hidePassword') : t('forms.password.showPassword')}
                >
                  <i className={`fa-solid ${showCurrent ? 'fa-eye-slash' : 'fa-eye'} text-sm`} />
                </button>
              </div>
            </div>
            <div>
              <label htmlFor="change-password-new" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('header.changePasswordNewLabel')}
              </label>
              <div className="relative">
                <input
                  id="change-password-new"
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent ${!showNew ? 'input-mask-password' : ''}`}
                  placeholder={t('header.changePasswordNewPlaceholder')}
                  autoComplete="off"
                  disabled={isSubmitting}
                />
                <button
                  type="button"
                  onClick={() => setShowNew((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                  aria-label={showNew ? t('forms.password.hidePassword') : t('forms.password.showPassword')}
                >
                  <i className={`fa-solid ${showNew ? 'fa-eye-slash' : 'fa-eye'} text-sm`} />
                </button>
              </div>
            </div>
            <div>
              <label htmlFor="change-password-confirm" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('header.changePasswordConfirmLabel')}
              </label>
              <input
                id="change-password-confirm"
                type="text"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary focus:border-transparent input-mask-password"
                placeholder={t('header.changePasswordConfirmPlaceholder')}
                autoComplete="off"
                disabled={isSubmitting}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin" />
                    {t('common.processing')}
                  </>
                ) : (
                  t('header.changePasswordSubmit')
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );

  return createPortal(modalContent, document.body);
};

export default ChangePasswordModal;
